import type { Pinia } from 'pinia';

import { useTrackingStore } from '@/stores/pinia/useTrackingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

import { createHttpLink } from 'apollo-link-http';
import { ApolloLink } from 'apollo-link';
import { RetryLink } from 'apollo-link-retry';
import { storeToRefs } from 'pinia';

export type ServerHeaders = { [key: string]: string };

export function getAppVersion() {
	return `${process.env.VERSION}-web-${process.server ? 'ssr' : 'web'}`;
}

type CustomHeaders = {
	'App-Version'?: string;
	'DEVICE-ID'?: string;
	'Experiments-Variants'?: string;
};

const withAccessToken = () =>
	new ApolloLink((operation, forward) => {
		const userStore = useUserStore();

		if (userStore.accessToken) {
			operation.setContext(({ headers }: { headers: any }) => ({
				headers: {
					...headers,
					Authorization: `Bearer ${userStore.accessToken}`,
				},
			}));
		}

		return forward(operation);
	});

const withAuthMiddleware = () =>
	new ApolloLink((operation, forward) => {
		/*
		This function will be called each time we make a GraphQL query or mutation.
		New users don't have an access token until they sign up.
		Since we cannot reinitialize ApolloClient once we get the access token and save it as a header,
		this code will be checking for it on each query or mutation.
	*/
		const userStore = useUserStore();
		const jwId = userStore.jwId;
		const customHeaders: CustomHeaders = {
			'App-Version': getAppVersion(),
		};

		if (jwId) {
			customHeaders['DEVICE-ID'] = jwId;
		}

		operation.setContext(({ headers }: { headers: any }) => ({
			headers: {
				...headers,
				...customHeaders,
			},
		}));

		return forward(operation);
	});

const withHeaders = (customHeaders?: ServerHeaders) =>
	new ApolloLink((operation, forward) => {
		operation.setContext(({ headers = {} }) => ({
			http: {
				includeExtensions: false,
				includeQuery: true,
			},
			headers: {
				...headers,
				...customHeaders,
			},
		}));

		return forward(operation);
	});

// HTTP connection to the API
const withHttpLink = (): ApolloLink =>
	createHttpLink({
		uri: process.server ? JW_CONFIG.GRAPHQL_INTERNAL_URL : JW_CONFIG.GRAPHQL_URL,
		fetch,
	});

const withSGHeader = (pinia: Pinia) => {
	const language = storeToRefs(useLanguageStore(pinia));
	const userStore = storeToRefs(useUserStore(pinia));
	const routingStore = useRoutingStore(pinia);
	const trackingStore = useTrackingStore(pinia);

	return new ApolloLink((operation, forward) => {
		const headerParams = {
			c: language.country.value ?? '',
			l: language.language.value ?? '',
			pv: trackingStore.pageViewUUID ?? '',
			d: userStore.jwId.value ?? '',
			p: getAppVersion() ?? '',
			pa: routingStore.activeRoute?.fullPath ?? '',
			e: (routingStore.activeRoute?.query?.new_preferred_variant as string | undefined) ?? '',
		};

		const header = new URLSearchParams(headerParams).toString();

		if (header) {
			operation.setContext(({ headers = {} }) => ({
				headers: {
					...headers,
					sg: header,
				},
			}));
		}
		return forward(operation);
	});
};

const withRetry = (): RetryLink =>
	new RetryLink({
		delay: {
			initial: 300, // ms
			max: 5000, // ms
			jitter: true, // randomize between initial and max
		},
		attempts: {
			max: 2,
			retryIf: (error, _operation) => {
				const { name, statusCode } = error;
				console.error('[RetryLink]', name, statusCode);
				return !!error;
			},
		},
	});

const withResponseLogger = (headers?: ServerHeaders): ApolloLink => {
	console.debug(`[DEBUG_APOLLO] Apollo Request/Response Logger Enabled`);
	try {
		console.debug(`[DEBUG_APOLLO] Apollo Headers`, JSON.stringify(headers));
	} catch {}

	return new ApolloLink((operation, forward) => {
		try {
			console.debug(
				'[DEBUG_APOLLO] Request',
				JSON.stringify({ isServer: process.server, context: operation.getContext() })
			);
			console.debug('\n');
		} catch {}

		return forward(operation).map(result => {
			try {
				console.debug(
					'[DEBUG_APOLLO] Response',
					JSON.stringify({ isServer: process.server, context: operation.getContext() })
				);
				console.debug('\n');
			} catch {}

			return result;
		});
	});
};

export { withAccessToken, withAuthMiddleware, withHeaders, withHttpLink, withResponseLogger, withRetry, withSGHeader };
